import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { useAppStore } from '@/store';
import { selectCoursesWithBudget } from '@/store/selectors';
import { CourseCardGrid } from './CourseCardGrid';
import { EmptyState, Button, Modal, Tooltip } from '../shared';
import { SemesterDashboard } from '../SemesterDashboard/SemesterDashboard';
import { DemoCourseLoader } from '../DemoCourse/DemoCourseLoader';
import { CourseSetupForm } from '../CourseSetup/CourseSetupForm';

export interface DashboardProps {
  onStartWizard: () => void;
}

export function Dashboard({ onStartWizard }: DashboardProps): React.ReactElement {
  const coursesWithBudget = useAppStore(selectCoursesWithBudget);
  const [showSemesterView, setShowSemesterView] = useState(false);
  const [showAdvancedSetup, setShowAdvancedSetup] = useState(false);

  if (coursesWithBudget.length === 0) {
    return (
      <main className="dashboard" id="main-content" aria-label="Course dashboard">
        <EmptyState
          icon={<BookOpen aria-hidden="true" />}
          title="Welcome to Absence Budget!"
          description="Click + Add Course to set up your first absence budget tracker."
          actionLabel="Add Course"
          onAction={onStartWizard}
        />
        <DemoCourseLoader />

        <Modal isOpen={showAdvancedSetup} onClose={() => setShowAdvancedSetup(false)} title="Manual Course Setup">
          <CourseSetupForm
            onSave={() => setShowAdvancedSetup(false)}
            onCancel={() => setShowAdvancedSetup(false)}
          />
        </Modal>
      </main>
    );
  }

  return (
    <main className="dashboard" id="main-content" aria-label="Course dashboard">
      <div className="dashboard__toolbar">
        <h2>Your Courses</h2>
        <div className="dashboard__actions">
          <Button variant="secondary" size="sm" onClick={() => setShowAdvancedSetup(true)}>
            Manual Setup
          </Button>
          <Tooltip content="Toggle between per-course and semester-wide attendance views">
            <Button
              variant={showSemesterView ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setShowSemesterView(!showSemesterView)}
            >
              {showSemesterView ? 'Course View' : 'Semester View'}
            </Button>
          </Tooltip>
        </div>
      </div>

      {showSemesterView ? (
        <SemesterDashboard />
      ) : (
        <CourseCardGrid coursesWithBudget={coursesWithBudget} onAddCourse={onStartWizard} />
      )}

      {!coursesWithBudget.some(({ course }) => course.name.startsWith('Demo')) && (
        <div className="dashboard__demo-section">
          <DemoCourseLoader />
        </div>
      )}

      <Modal isOpen={showAdvancedSetup} onClose={() => setShowAdvancedSetup(false)} title="Manual Course Setup">
        <CourseSetupForm
          onSave={() => setShowAdvancedSetup(false)}
          onCancel={() => setShowAdvancedSetup(false)}
        />
      </Modal>
    </main>
  );
}
